// Path: ./src/app/pages/MerchantPage/useMerchantTableColumns.tsx
// setTableColumn.tsx

import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  GfMerchantDto,
  GfMerchant,
  SettingKey,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { RenderMultiImageTable } from "../../components/RenderImageTable";
import { useMemo } from "react";
import {
  useTableSettingsConfig,
  applyColumnsConfig,
} from "../../../hooks/useTableSettingsConfig";
import { routes } from "../../../utils/constant/navigationRoutes";

export const useMerchantTableColumns = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { columnsConfig } = useTableSettingsConfig(SettingKey.TableMerchants);
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];
  /**
   * Handles the click event to navigate to the merchant editing page with pre-filled data.
   *
   * @param value - The EditProps containing merchant details for editing.
   */
  const handleOnClickEdit = async (value: GfMerchantDto) => {
    navigate(routes.merchant.editById(value?.id || ""));
  };

  const baseColumns = [
    {
      field: "edit",
      filterable: false,
      width: 90,
      headerName: `${t(messages.Edit())}`,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["merchant"]}
          >
            <RenderEditButton
              onClick={handleOnClickEdit}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      filterable: false,
      field: "gfImages",
      headerName: `${t(messages["gfImages"]())}`,
      renderCell: (props: GridRenderCellParams<GfMerchantDto>) => {
        return (
          <RenderMultiImageTable
            images={[
              props.row.merchantLogo,
              props.row.merchantMapLargeLogo,
              props.row.merchantMapSmallLogo,
            ]}
          ></RenderMultiImageTable>
        );
      },
      sortable: false,
    },
    {
      field: "name",
      width: 200,
      headerName: `${t(messages["name"]())}`,
    },
    {
      field: "domain",
      width: 300,
      headerName: `${t(messages["domain"]())}`,
    },
    {
      field: "aiCoordinationMode",
      width: 200,
      type: "singleSelect",
      valueOptions: [
        {
          value: GfMerchant.AiCoordinationModeEnum.AI,
          label: t(messages.AI()),
        },
        {
          value: GfMerchant.AiCoordinationModeEnum.AIREFINEMENT,
          label: t(messages.AI_REFINEMENT()),
        },
      ],
      headerName: `${t(messages.AI_Coordination_Mode())}`,
      sortable: true,
      renderCell: (params: GridRenderCellParams<GfMerchantDto>) => {
        const mode = params.row.aiCoordinationMode;
        if (!mode) {
          return <div>{t(messages.Off())}</div>;
        }
        if (mode === GfMerchant.AiCoordinationModeEnum.AI) {
          return <div>{t(messages.AI())}</div>;
        }
        if (mode === GfMerchant.AiCoordinationModeEnum.AIREFINEMENT) {
          return <div>{t(messages.AI_REFINEMENT())}</div>;
        }
        return <div>{mode}</div>;
      },
    },
    {
      field: "smartFlyerModelOverride",
      width: 200,
      type: "singleSelect",
      // Object.values() also yields the enum's reverse-mapping (PascalCase)
      // keys; real model values are lowercase-kebab, so keep only those.
      valueOptions: (
        Object.values(
          GfMerchant.SmartFlyerModelOverrideEnum,
        ) as GfMerchant.SmartFlyerModelOverrideEnum[]
      )
        .filter((value) => String(value) === String(value).toLowerCase())
        .map((value) => ({ value, label: String(value) })),
      headerName: `${t(messages.Smart_Flyer_Model())}`,
      sortable: true,
      renderCell: (params: GridRenderCellParams<GfMerchantDto>) => {
        const model = params.row.smartFlyerModelOverride;
        if (!model) {
          return <div>{t(messages.Use_System_Default())}</div>;
        }
        return <div>{model}</div>;
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "id",
      filterable: false,
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
  ];

  const columns = useMemo(
    () => applyColumnsConfig(baseColumns, columnsConfig),
    [baseColumns, columnsConfig],
  );

  return columns;
};
